/**
 * Admin · Carriers (§21, §7 / §B-2). MANAGER sees the read-only registry table
 * (adapters + services + credential status). ADMIN manages CarrierConfigRow rows:
 * create / edit / delete + the §7 test-label action. Credential values are never
 * shown — only key names + status. Writes go through the ADMIN API (audited,
 * optimistic concurrency, credentials encrypted at rest).
 */
import { useCallback, useMemo, useState } from "react";
import { api, type AdminCarrier, type AdminCarrierConfig } from "../../lib/api.js";
import { useResource } from "../../lib/useResource.js";
import { useSession } from "../../components/auth.js";
import { humanizeCode, humanizeServiceCode } from "../../lib/format.js";
import { formatAge } from "../../lib/age.js";
import {
  Badge,
  StateChip,
  Tag,
  CarrierMark,
  DataTable,
  type Column,
  Button,
  EmptyState,
  ErrorState,
  SkeletonRows,
  Icon,
} from "../../components/ui/index.js";
import { CarrierConfigEditor } from "./carriers/CarrierConfigEditor.js";

/** The carrier's live integration state — one scannable status chip. */
function StatusChip({ c }: { c: AdminCarrierConfig }) {
  if (c.credentialStatus !== "present") return <span className="chip chip--cancelled"><span className="chip__dot" aria-hidden="true" />Not connected</span>;
  if (c.productionReady) return <span className="chip chip--ready"><span className="chip__dot" aria-hidden="true" />Active</span>;
  if (c.testLabelOk) return <span className="chip chip--cancelled"><span className="chip__dot" aria-hidden="true" />Connected · paused</span>;
  return <span className="chip chip--hold"><span className="chip__dot" aria-hidden="true" />Connected · setup incomplete</span>;
}

/** One-line test-state summary (connection + last test-label), relative time. */
function testStateLine(c: AdminCarrierConfig, now: number): string {
  if (c.credentialStatus !== "present") return "No credentials stored";
  const conn = c.testConnectionOk ? "connection ✓" : "connection not checked";
  const label = c.testLabelOk
    ? `test-label passed${c.testLabelAt ? ` · ${formatAge(c.testLabelAt, now)}` : ""}`
    : "test-label not passed";
  return `${conn} · ${label}`;
}

const REGISTRY_COLUMNS: Array<Column<AdminCarrier>> = [
  {
    key: "carrier",
    header: "Carrier",
    cell: (c) => (
      <span className="cell-lead">
        <CarrierMark code={c.code} size={26} />
        <span className="cell-stack">
          <span className="cell-strong">{c.displayName}</span>
          <span className="cell-sub">{c.trackingCompany ?? "—"}</span>
        </span>
      </span>
    ),
  },
  {
    key: "services",
    header: "Services",
    cell: (c) =>
      c.services.length > 0 ? (
        <span className="chip-row">
          {c.services.map((s) => (
            <Tag key={s.code}>{humanizeServiceCode(s.code)}</Tag>
          ))}
        </span>
      ) : (
        <span className="muted">—</span>
      ),
  },
  { key: "creds", header: "Credentials", cell: (c) => (c.credentialStatus === "present" ? <Badge>present</Badge> : <span className="muted">missing</span>) },
  { key: "enabled", header: "Status", cell: (c) => <StateChip on={c.enabled} /> },
];

export function Carriers() {
  const { user } = useSession();
  const isAdmin = user?.role === "ADMIN";
  const r = useResource(api.admin.carriers);
  const storesRes = useResource(api.admin.stores);
  const [editing, setEditing] = useState<AdminCarrierConfig | null | "new">(null);
  const now = Date.now();

  const reload = r.reload;
  const carriers = useMemo(() => r.data?.carriers ?? [], [r.data]);
  const configs = useMemo(() => r.data?.configs ?? [], [r.data]);
  const credentialFields = r.data?.credentialFields ?? {};
  const carrierCodes = carriers.map((c) => c.code);
  const stores = useMemo(() => (storesRes.data?.stores ?? []).map((s) => ({ id: s.id, label: `${s.name} (${s.region})` })), [storesRes.data]);
  const storeLabel = useCallback((id: string | null) => (id ? (stores.find((s) => s.id === id)?.label ?? id) : "All stores"), [stores]);

  // ---- read-only view (MANAGER) ----
  if (!isAdmin) {
    return (
      <section className="admin">
        <div className="page-head">
          <div className="page-head__title">
            <h1>Carriers</h1>
            {r.data && <span className="page-head__count">{carriers.length} registered</span>}
          </div>
          <span className="soon" title="Admin role required to edit">Read-only · admin can edit</span>
        </div>
        {r.error ? (
          <ErrorState title="Couldn’t load this" hint="Retry in a moment." onRetry={r.reload} />
        ) : r.loading && !r.data ? (
          <div className="table-card"><SkeletonRows rows={4} cols={4} /></div>
        ) : carriers.length === 0 ? (
          <EmptyState icon="truck" title="No carriers registered" />
        ) : (
          <DataTable ariaLabel="Carriers" rows={carriers} getRowKey={(c) => c.code} columns={REGISTRY_COLUMNS} />
        )}
      </section>
    );
  }

  // ---- editor view (ADMIN) ----
  return (
    <section className="admin">
      <div className="page-head">
        <div className="page-head__title">
          <h1>Carriers</h1>
          {r.data && (
            <span className="page-head__count">
              {configs.length} configuration{configs.length === 1 ? "" : "s"} · {carriers.length} adapters
            </span>
          )}
        </div>
        <div className="page-head__actions">
          <Button variant="primary" size="sm" icon="plus" onClick={() => setEditing("new")}>
            Configure carrier
          </Button>
        </div>
      </div>

      {r.error ? (
        <ErrorState title="Couldn’t load this" hint="Retry in a moment." onRetry={r.reload} />
      ) : r.loading && !r.data ? (
        <div className="table-card"><SkeletonRows rows={4} cols={6} /></div>
      ) : configs.length === 0 ? (
        <EmptyState icon="truck" title="No carrier configurations" hint="Add credentials for a carrier (GLS, DPD, DHL, FedEx) so it can create labels." />
      ) : (
        <ul className="rule-list">
          {configs.map((c) => (
            <li key={c.id} className={"rule-row rule-row--clickable" + (c.enabled ? "" : " rule-row--off")}>
              <button type="button" className="rule-row__open" onClick={() => setEditing(c)} aria-label={`Manage ${humanizeCode(c.carrierCode)} (${storeLabel(c.storeId)})`}>
                <div className="rule-row__body">
                  <span className="rule-row__name cell-lead">
                    <CarrierMark code={c.carrierCode} />
                    <span>{humanizeCode(c.carrierCode)} <span className="cell-sub">· {storeLabel(c.storeId)}</span></span>
                  </span>
                  <span className="cell-sub">{testStateLine(c, now)}</span>
                </div>
                <div className="rule-row__actions">
                  <StatusChip c={c} />
                  <span className="rule-row__chevron" aria-hidden="true"><Icon name="chevronRight" size={16} /></span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {editing !== null && (
        <CarrierConfigEditor
          config={editing === "new" ? null : editing}
          carrierCodes={carrierCodes}
          credentialFields={credentialFields}
          stores={stores}
          onClose={() => setEditing(null)}
          onChanged={reload}
        />
      )}
    </section>
  );
}
